/**
 * Operations on a whole diagram.
 *
 * Separated from the React surface so that every change to a document is
 * a pure function of the document and the change — which is what makes
 * undo a snapshot stack rather than a list of inverse operations somebody
 * has to remember to extend. The day an inverse is forgotten, undo
 * quietly corrupts the chart, and on a chain of title that is a wrong
 * answer about who owns land.
 */
import { boundsOf, outerBounds, rectsIntersect } from './geometry.js';
import { makeShape, makeEdge } from './model.js';

export const byId = (list) => new Map(list.map((item) => [item.id, item]));

/** Topmost shape under a point, or null. Reverse z, so the top wins. */
export function shapeAt(doc, point, hit) {
  const ordered = [...doc.shapes].sort((a, b) => b.z - a.z);
  for (const shape of ordered) if (hit(shape, point)) return shape;
  return null;
}

/**
 * Everything a marquee caught.
 *
 * TOUCHES, not contains. Enclosing a shape entirely is the convention in
 * drawing tools and it is the wrong one here: the boxes on a chain of
 * title are wide, so reaching round three of them means starting off the
 * edge of the chart and dragging past everything in between, and a band
 * that clipped two of the three took neither. Brushing a shape selects
 * it, which is the rule people arrive with from Figma and Miro and the
 * one that matches what the band looks like it is doing.
 *
 * CONNECTORS ARE CAUGHT THE SAME WAY. They could not be caught at all
 * before — a band drawn round a branch took the parties and left every
 * deed between them, so moving that branch tore the arrows off it. A
 * connector counts when any part of its run crosses the band;
 * `pointsFor` hands back the route because where a line actually goes is
 * solved on the screen, not stored on the edge.
 */
export function marqueeSelect(doc, rect, pointsFor = null) {
  const ids = [];
  for (const shape of doc.shapes) if (rectsIntersect(rect, outerBounds(shape))) ids.push(shape.id);
  if (!pointsFor) return ids;
  for (const edge of doc.edges) {
    const points = pointsFor(edge.id);
    if (!points || points.length < 2) continue;
    if (points.some((p, i) => i > 0 && segmentHitsRect(points[i - 1], p, rect))) ids.push(edge.id);
  }
  return ids;
}

/** Does a line segment touch the rect — either end inside, or crossing it? */
function segmentHitsRect(a, b, rect) {
  const inside = (p) => p.x >= rect.x && p.x <= rect.x + rect.w
    && p.y >= rect.y && p.y <= rect.y + rect.h;
  if (inside(a) || inside(b)) return true;
  // Every leg of an elbow route is square, so a crossing is a span test
  // on one axis against the band's extent on the other.
  const [x1, x2] = [Math.min(a.x, b.x), Math.max(a.x, b.x)];
  const [y1, y2] = [Math.min(a.y, b.y), Math.max(a.y, b.y)];
  return x1 <= rect.x + rect.w && x2 >= rect.x && y1 <= rect.y + rect.h && y2 >= rect.y;
}

/** Anything touching the rect at all — used by the eraser and by tests. */
export function touching(doc, rect) {
  return doc.shapes.filter((s) => rectsIntersect(rect, outerBounds(s))).map((s) => s.id);
}

/**
 * A selection widened to whole groups.
 *
 * Clicking one box of a group selects the group, because that is what a
 * group is for; the alternative — a click that quietly takes one shape
 * out of its group — is how grouped diagrams come apart.
 */
export function withGroups(doc, ids) {
  const chosen = new Set(ids);
  const groups = new Set();
  for (const shape of doc.shapes) if (chosen.has(shape.id) && shape.groupId) groups.add(shape.groupId);
  if (!groups.size) return [...chosen];
  for (const shape of doc.shapes) if (shape.groupId && groups.has(shape.groupId)) chosen.add(shape.id);
  return [...chosen];
}

export function selectedShapes(doc, ids) {
  const chosen = new Set(ids);
  return doc.shapes.filter((s) => chosen.has(s.id));
}

export function selectionBounds(doc, ids) {
  return boundsOf(selectedShapes(doc, ids).map(outerBounds));
}

/* ----------------------------------------------------------- edits -- */

export function updateShapes(doc, ids, patch) {
  const chosen = new Set(ids);
  return {
    ...doc,
    shapes: doc.shapes.map((s) => (chosen.has(s.id)
      ? { ...s, ...(typeof patch === 'function' ? patch(s) : patch) }
      : s)),
  };
}

export function updateEdges(doc, ids, patch) {
  const chosen = new Set(ids);
  return {
    ...doc,
    edges: doc.edges.map((e) => (chosen.has(e.id)
      ? { ...e, ...(typeof patch === 'function' ? patch(e) : patch) }
      : e)),
  };
}

export function moveShapes(doc, ids, dx, dy) {
  return updateShapes(doc, ids, (s) => ({ x: s.x + dx, y: s.y + dy }));
}

export function addShape(doc, patch) {
  const shape = makeShape({ ...patch, z: doc.nextZ });
  return { doc: { ...doc, shapes: [...doc.shapes, shape], nextZ: doc.nextZ + 1 }, shape };
}

export function addEdge(doc, patch) {
  const edge = makeEdge({ ...patch, z: doc.nextZ });
  return { doc: { ...doc, edges: [...doc.edges, edge], nextZ: doc.nextZ + 1 }, edge };
}

/**
 * Delete, taking connectors with the shapes they hang off.
 *
 * A connector whose end has gone is a line from nowhere. It is removed
 * rather than left dangling at its last coordinates, which is what makes
 * a deleted party leave a clean gap in the chain instead of an arrow
 * pointing into space.
 */
export function removeItems(doc, ids) {
  const gone = new Set(ids);
  const shapes = doc.shapes.filter((s) => !gone.has(s.id));
  const alive = new Set(shapes.map((s) => s.id));
  const edges = doc.edges.filter((e) => {
    if (gone.has(e.id)) return false;
    for (const end of [e.from, e.to]) {
      if (end?.shapeId && !alive.has(end.shapeId)) return false;
    }
    return true;
  });
  return { ...doc, shapes, edges };
}

/**
 * Copy, offset so the copy is visibly a copy.
 *
 * Connectors BETWEEN duplicated shapes are duplicated too and rewired to
 * the copies; a connector to something outside the selection is not,
 * because the copy did not inherit that relationship — duplicating three
 * heirs should not give you three more arrows into the same deed.
 */
export function duplicate(doc, ids, dx = 24, dy = 24) {
  const chosen = new Set(ids);
  const remap = new Map();
  let next = doc.nextZ;

  const shapes = doc.shapes.filter((s) => chosen.has(s.id)).map((s) => {
    const copy = makeShape({ ...s, id: undefined, x: s.x + dx, y: s.y + dy, z: next });
    next += 1;
    remap.set(s.id, copy.id);
    return copy;
  });

  const edges = doc.edges.filter((e) => (
    e.from?.shapeId && e.to?.shapeId && remap.has(e.from.shapeId) && remap.has(e.to.shapeId)
  )).map((e) => {
    const copy = makeEdge({
      ...e,
      id: undefined,
      from: { ...e.from, shapeId: remap.get(e.from.shapeId) },
      to: { ...e.to, shapeId: remap.get(e.to.shapeId) },
      z: next,
    });
    next += 1;
    return copy;
  });

  return {
    doc: { ...doc, shapes: [...doc.shapes, ...shapes], edges: [...doc.edges, ...edges], nextZ: next },
    ids: shapes.map((s) => s.id),
  };
}

/**
 * The selection, as something that can be put back.
 *
 * Connectors come too, but only those BETWEEN copied shapes. A connector
 * to something outside the selection did not survive the copy — the copy
 * does not hold that relationship — and pasting it would attach a new
 * arrow to an old box, which on a chain of title is a conveyance nobody
 * recorded.
 */
export function copyItems(doc, ids) {
  const chosen = new Set(ids);
  const shapes = doc.shapes.filter((s) => chosen.has(s.id));
  const kept = new Set(shapes.map((s) => s.id));
  const edges = doc.edges.filter((e) => (
    e.from?.shapeId && e.to?.shapeId && kept.has(e.from.shapeId) && kept.has(e.to.shapeId)
  ));
  return { shapes, edges };
}

/**
 * Put a copy on the canvas.
 *
 * Fresh ids, connectors rewired to the copies, and the whole thing
 * offset so it is visibly a copy rather than sitting exactly on the
 * original where nobody can tell one was made.
 *
 * `at` places the paste where the pointer is — the habit everywhere —
 * and falls back to a small offset when there is nowhere in particular,
 * which is what a keyboard paste means.
 */
export function pasteItems(doc, clip, { at = null, offset = 24 } = {}) {
  const source = clip?.shapes || [];
  if (!source.length) return { doc, ids: [] };

  const box = boundsOf(source.map(outerBounds));
  const dx = at ? Math.round(at.x - box.w / 2 - box.x) : offset;
  const dy = at ? Math.round(at.y - box.h / 2 - box.y) : offset;

  const remap = new Map();
  let next = doc.nextZ;
  const shapes = source.map((s) => {
    const copy = makeShape({ ...s, id: undefined, x: s.x + dx, y: s.y + dy, z: next });
    next += 1;
    remap.set(s.id, copy.id);
    return copy;
  });
  const edges = (clip.edges || []).map((e) => {
    const copy = makeEdge({
      ...e,
      id: undefined,
      from: { ...e.from, shapeId: remap.get(e.from.shapeId) },
      to: { ...e.to, shapeId: remap.get(e.to.shapeId) },
      z: next,
    });
    next += 1;
    return copy;
  });

  return {
    doc: { ...doc, shapes: [...doc.shapes, ...shapes], edges: [...doc.edges, ...edges], nextZ: next },
    ids: shapes.map((s) => s.id),
  };
}

/* ------------------------------------------------------- arranging -- */

export function bringToFront(doc, ids) {
  let next = doc.nextZ;
  const chosen = new Set(ids);
  const shapes = doc.shapes.map((s) => (chosen.has(s.id) ? { ...s, z: next++ } : s));
  return { ...doc, shapes, nextZ: next };
}

export function sendToBack(doc, ids) {
  const chosen = new Set(ids);
  const lowest = Math.min(0, ...doc.shapes.map((s) => s.z));
  let next = lowest - ids.length;
  const shapes = doc.shapes.map((s) => (chosen.has(s.id) ? { ...s, z: next++ } : s));
  return { ...doc, shapes };
}

/**
 * Align a selection to its own bounds.
 *
 * To the BOUNDS rather than to the first-selected shape: selection order
 * is invisible on screen, so aligning to it makes the result depend on
 * something the person cannot see.
 */
export function align(doc, ids, how) {
  const box = selectionBounds(doc, ids);
  if (!box || ids.length < 2) return doc;
  return updateShapes(doc, ids, (s) => {
    switch (how) {
      case 'left': return { x: box.x };
      case 'right': return { x: box.x + box.w - s.w };
      case 'hcenter': return { x: box.x + (box.w - s.w) / 2 };
      case 'top': return { y: box.y };
      case 'bottom': return { y: box.y + box.h - s.h };
      case 'vcenter': return { y: box.y + (box.h - s.h) / 2 };
      default: return {};
    }
  });
}

/** Equal gaps between three or more, along one axis. */
export function distribute(doc, ids, axis) {
  const chosen = selectedShapes(doc, ids);
  if (chosen.length < 3) return doc;
  const lead = axis === 'x' ? 'x' : 'y';
  const size = axis === 'x' ? 'w' : 'h';
  const ordered = [...chosen].sort((a, b) => a[lead] - b[lead]);
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  const span = (last[lead] + last[size]) - first[lead];
  const used = ordered.reduce((sum, s) => sum + s[size], 0);
  const gap = (span - used) / (ordered.length - 1);

  let at = first[lead];
  const moves = new Map();
  for (const shape of ordered) {
    moves.set(shape.id, at);
    at += shape[size] + gap;
  }
  return updateShapes(doc, ids, (s) => (moves.has(s.id) ? { [lead]: moves.get(s.id) } : {}));
}

let groupSeq = 0;
export function group(doc, ids) {
  if (ids.length < 2) return doc;
  groupSeq += 1;
  const name = `g${Date.now().toString(36)}${groupSeq.toString(36)}`;
  return updateShapes(doc, ids, { groupId: name });
}

export function ungroup(doc, ids) {
  return updateShapes(doc, ids, { groupId: null });
}
